import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { Button, Input, type InputRef, Modal } from 'antd';
import {
  type CSSProperties,
  type FC,
  type KeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from 'src/lib/customHooks/useCurrentUser';
import { useHotKeyListener } from 'src/lib/customHooks/useHotKeyListener';
import { Platforms, getPlatformOfUserAgent } from 'src/lib/userAgent';
import { useOnClickOutside } from 'usehooks-ts';
import { KeyCap } from '../BasicComponents/Hotkeys';
import { IconSearch } from '../BasicComponents/Icons';
import { SearchPanel, optionId } from './SearchPanel';
import { useRecentSearches } from './useRecentSearches';
import { type SearchGroup, type SearchItem, useSearchResults } from './useSearchResults';

interface Props {
  /** Header is in its collapsed layout: show a trigger that takes over the screen. */
  compact?: boolean;
}

export const GlobalSearch: FC<Props> = ({ compact = false }) => {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const listboxId = useId();
  const inputRef = useRef<InputRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const isMac = getPlatformOfUserAgent(navigator.userAgent) === Platforms.MAC;

  const {
    groups: eventGroups,
    eventItems,
    debouncedQuery,
    isActive,
    isLoading,
    hasError,
    retry,
    hasNextPage,
    loadNextPage,
  } = useSearchResults(query);
  // Keyed by user: a shared browser must not show one account's queries to the next.
  const { recentSearches, rememberSearch } = useRecentSearches(currentUser?.id ?? null);

  const recentItems: Array<SearchItem> = recentSearches.map((recent) => ({
    kind: 'recent' as const,
    key: `recent-${recent}`,
    query: recent,
  }));
  const recentGroups: Array<SearchGroup> =
    recentItems.length > 0 ? [{ key: 'recent', title: 'Recent', items: recentItems }] : [];
  const groups = isActive ? eventGroups : recentGroups;
  const items: Array<SearchItem> = isActive ? eventItems : recentItems;
  const isPanelOpen = isOpen && (isActive || items.length > 0);
  const activeOptionId =
    isPanelOpen && items.length > 0 ? optionId(listboxId, activeIndex) : undefined;

  const focusInput = () => inputRef.current?.focus({ cursor: 'end' });
  const open = () => (compact ? setIsOverlayOpen(true) : focusInput());

  // Two listeners rather than one: useHotKeyListener rejects a modifier it was not asked for,
  // so ⌘K on a Mac and Ctrl+K everywhere else have to be registered separately.
  useHotKeyListener('k', open, true);
  useHotKeyListener('k', open, false, false, true);

  // Empty is excluded: opening a result clears the field, and re-opening on that would leave the
  // recents panel over the page the result navigated to.
  useEffect(() => {
    setActiveIndex(0);
    if (query !== '') setIsOpen(true);
  }, [query]);

  useOnClickOutside(containerRef, () => setIsOpen(false));

  const closeOverlay = () => {
    setIsOverlayOpen(false);
    setIsOpen(false);
    setQuery('');
  };

  const handleSelect = (item: SearchItem) => {
    if (item.kind === 'recent') {
      setQuery(item.query);
      focusInput();
      return;
    }

    // Remembered here rather than on every search: a query nobody acted on is not worth offering.
    rememberSearch(debouncedQuery);
    setQuery('');
    setIsOverlayOpen(false);
    setIsOpen(false);
    inputRef.current?.blur();
    navigate(item.path);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      if (isOverlayOpen) closeOverlay();
      else if (isPanelOpen) setIsOpen(false);
      else inputRef.current?.blur();
      return;
    }
    if (!isPanelOpen || items.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % items.length);
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + items.length) % items.length);
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const item = items[activeIndex];
      if (item != null) handleSelect(item);
    }
  };

  const panel = (
    <SearchPanel
      groups={groups}
      query={debouncedQuery}
      activeIndex={activeIndex}
      isLoading={isLoading}
      hasError={hasError}
      onRetry={retry}
      hasNextPage={hasNextPage}
      onLoadNextPage={loadNextPage}
      onSelect={handleSelect}
      onHover={setActiveIndex}
      listboxId={listboxId}
      fullWidth={compact}
    />
  );

  const searchInput = (
    <SearchInput
      ref={inputRef}
      value={query}
      placeholder="Search events..."
      variant={compact ? 'borderless' : 'outlined'}
      role="combobox"
      aria-label="Search events"
      aria-autocomplete="list"
      aria-expanded={isPanelOpen}
      aria-controls={listboxId}
      aria-activedescendant={activeOptionId}
      prefix={<IconSearch color={RacemapColors.DarkGray} />}
      suffix={!compact && <KeyCap>{isMac ? '⌘K' : 'Ctrl K'}</KeyCap>}
      styles={{ suffix: { marginInlineStart: 6 } }}
      onChange={(event) => setQuery(event.target.value)}
      onKeyDown={handleKeyDown}
      onFocus={() => setIsOpen(true)}
      allowClear
    />
  );

  if (compact) {
    return (
      <>
        <Button aria-label="Search" icon={<IconSearch />} type="primary" ghost onClick={open} />
        <SheetModal
          open={isOverlayOpen}
          onCancel={closeOverlay}
          closable={false}
          footer={null}
          destroyOnHidden
          // Not an effect on `open`: the dialog mounts its content behind an animation, so the
          // input does not exist yet when the state flips.
          afterOpenChange={(opened) => opened && focusInput()}
          styles={{ wrapper: { padding: 0 }, container: SheetContainerStyle }}
        >
          <SheetField>{searchInput}</SheetField>
          {isPanelOpen && panel}
        </SheetModal>
      </>
    );
  }

  return (
    <DesktopContainer ref={containerRef}>
      {searchInput}
      {isPanelOpen && <PanelAnchor>{panel}</PanelAnchor>}
    </DesktopContainer>
  );
};

// The header bar's only flexing child, so it absorbs the whole squeeze and nothing else wraps.
const DesktopContainer = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-width: 140px;
  max-width: 440px;
`;

// Right-anchored so the panel grows leftwards, landing nearer the middle of the screen.
const PanelAnchor = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 1001;
`;

const SearchInput = styled(Input)``;

// One card: the field is its first row and the results share that surface, not a dropdown below it.
const SheetModal = styled(Modal)`
  top: 0;
  margin: 0 !important;
  padding-bottom: 0;
  max-width: none !important;
  width: 100vw !important;
`;

// Passed through Modal's `styles.container` rather than a `.ant-modal-content` rule, which antd 6
// no longer emits.
const SheetContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '100vh',
  borderRadius: 0,
  padding: 0,
};

const SheetField = styled.div`
  flex: none;
  padding: 4px 6px;
  border-bottom: 1px solid ${RacemapColors.LightGray};
`;
